import type { CreatePerformanceAlertRequest, PerformanceAlert } from "@/types"
import { create } from "zustand"

// Mock data
const mockServers = [
  { id: "i-1234567890abcdef0", name: "web-server-01" },
  { id: "i-0987654321fedcba0", name: "db-server-01" },
  { id: "i-abcdef1234567890", name: "api-server-01" },
  { id: "i-fedcba0987654321", name: "cache-server-01" },
  { id: "i-1a2b3c4d5e6f7g8h", name: "analytics-server-01" },
  { id: "i-9h8g7f6e5d4c3b2a", name: "ml-training-01" },
]

const mockPerformanceAlerts: PerformanceAlert[] = [
  {
    id: "1",
    server_name: "web-server-01",
    server_id: "i-1234567890abcdef0",
    metric_type: "cpu",
    threshold: 80,
    start_date: new Date("2024-12-01").toISOString(),
    end_date: null,
    actions: ["email", "sms"],
    email_recipients: "ops@example.com",
    phone_number: "+1234567890",
    status: "active",
    current_value: 72.5,
    created_at: new Date("2024-12-01").toISOString(),
    updated_at: new Date("2024-12-01").toISOString(),
  },
  {
    id: "2",
    server_name: "db-server-01",
    server_id: "i-0987654321fedcba0",
    metric_type: "memory",
    threshold: 90,
    start_date: new Date("2024-12-05").toISOString(),
    end_date: null,
    actions: ["email", "restart"],
    email_recipients: "dba@example.com",
    status: "active",
    current_value: 85.3,
    created_at: new Date("2024-12-05").toISOString(),
    updated_at: new Date("2024-12-05").toISOString(),
  },
  {
    id: "3",
    server_name: "api-server-01",
    server_id: "i-abcdef1234567890",
    metric_type: "disk",
    threshold: 85,
    start_date: new Date("2024-11-20").toISOString(),
    end_date: new Date("2025-05-20").toISOString(),
    actions: ["email"],
    email_recipients: "devops@example.com",
    status: "active",
    current_value: 65.8,
    created_at: new Date("2024-11-20").toISOString(),
    updated_at: new Date("2024-12-01").toISOString(),
  },
  {
    id: "4",
    server_name: "cache-server-01",
    server_id: "i-fedcba0987654321",
    metric_type: "network",
    threshold: 100,
    start_date: new Date("2024-12-10").toISOString(),
    end_date: null,
    actions: ["email"],
    email_recipients: "ops@example.com",
    status: "inactive",
    current_value: 45.2,
    created_at: new Date("2024-12-10").toISOString(),
    updated_at: new Date("2024-12-10").toISOString(),
  },
  {
    id: "5",
    server_name: "analytics-server-01",
    server_id: "i-1a2b3c4d5e6f7g8h",
    metric_type: "cpu",
    threshold: 75,
    start_date: new Date("2024-12-15").toISOString(),
    end_date: new Date("2025-06-15").toISOString(),
    actions: ["email", "sms"],
    email_recipients: "analytics@example.com",
    phone_number: "+1987654321",
    status: "active",
    current_value: 78.9,
    created_at: new Date("2024-12-15").toISOString(),
    updated_at: new Date("2024-12-15").toISOString(),
  },
  {
    id: "6",
    server_name: "ml-training-01",
    server_id: "i-9h8g7f6e5d4c3b2a",
    metric_type: "memory",
    threshold: 95,
    start_date: new Date("2024-12-01").toISOString(),
    end_date: null,
    actions: ["email"],
    email_recipients: "ml-team@example.com",
    status: "active",
    current_value: 92.1,
    created_at: new Date("2024-12-01").toISOString(),
    updated_at: new Date("2024-12-20").toISOString(),
  },
]

const mockAlertHistory = [
  {
    id: "1",
    alert_id: "1",
    server_name: "web-server-01",
    metric_type: "cpu" as const,
    triggered_at: new Date("2025-01-15T10:30:00Z").toISOString(),
    value_at_trigger: 82.5,
    threshold: 80,
    duration: 5,
    actions_taken: ["email"],
    status: "resolved" as const,
    details: "Email sent to ops@example.com",
  },
  {
    id: "2",
    alert_id: "2",
    server_name: "db-server-01",
    metric_type: "memory" as const,
    triggered_at: new Date("2025-01-20T14:45:00Z").toISOString(),
    value_at_trigger: 91.0,
    threshold: 90,
    duration: 10,
    actions_taken: ["email", "restart"],
    status: "resolved" as const,
    details: "Server restarted automatically after email notification",
  },
  {
    id: "3",
    alert_id: "5",
    server_name: "analytics-server-01",
    metric_type: "cpu" as const,
    triggered_at: new Date("2025-01-22T09:15:00Z").toISOString(),
    value_at_trigger: 76.5,
    threshold: 75,
    duration: 10,
    actions_taken: ["email"],
    status: "triggered" as const,
    details: "Email notification sent",
  },
]

// Store interface
interface PerformanceAlertStore {
  // State
  alerts: PerformanceAlert[]
  servers: { id: string; name: string }[]
  history: {
    id: string
    alert_id: string
    server_name: string
    metric_type: "cpu" | "memory" | "disk" | "network"
    triggered_at: string
    value_at_trigger: number
    threshold: number
    duration: number
    actions_taken: string[]
    status: "triggered" | "resolved" | "failed"
    details?: string
  }[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchAlerts: () => Promise<void>
  fetchServers: () => Promise<void>
  createAlert: (data: CreatePerformanceAlertRequest) => Promise<void>
  updateAlert: (id: string, data: CreatePerformanceAlertRequest) => Promise<void>
  deleteAlert: (id: string) => Promise<void>
  toggleAlertStatus: (id: string) => Promise<void>
  reset: () => void
}

export const usePerformanceAlertStore = create<PerformanceAlertStore>((set, get) => ({
  // Initial state
  alerts: [],
  servers: [],
  history: mockAlertHistory,
  isLoading: false,
  error: null,

  // Actions
  fetchAlerts: async () => {
    set({ isLoading: true, error: null })
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))
      set({ alerts: mockPerformanceAlerts, isLoading: false })
    } catch (error: any) {
      set({ error: error.message || "Failed to fetch alerts", isLoading: false })
    }
  },

  fetchServers: async () => {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300))
      set({ servers: mockServers })
    } catch (error: any) {
      console.error("Failed to fetch servers:", error)
    }
  },

  createAlert: async (data: CreatePerformanceAlertRequest) => {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      const newAlert: PerformanceAlert = {
        id: String(Date.now()),
        ...data,
        current_value: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      set({ alerts: [...get().alerts, newAlert] })
    } catch (error: any) {
      throw new Error(error.message || "Failed to create alert")
    }
  },

  updateAlert: async (id: string, data: CreatePerformanceAlertRequest) => {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      set({
        alerts: get().alerts.map((alert) =>
          alert.id === id
            ? {
                ...alert,
                ...data,
                updated_at: new Date().toISOString(),
              }
            : alert
        ),
      })
    } catch (error: any) {
      throw new Error(error.message || "Failed to update alert")
    }
  },

  deleteAlert: async (id: string) => {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      set({
        alerts: get().alerts.filter((alert) => alert.id !== id),
      })
    } catch (error: any) {
      throw new Error(error.message || "Failed to delete alert")
    }
  },

  toggleAlertStatus: async (id: string) => {
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300))

      set({
        alerts: get().alerts.map((alert) =>
          alert.id === id
            ? {
                ...alert,
                status: alert.status === "active" ? "inactive" : "active",
                updated_at: new Date().toISOString(),
              }
            : alert
        ),
      })
    } catch (error: any) {
      throw new Error(error.message || "Failed to toggle alert status")
    }
  },

  reset: () =>
    set({
      alerts: [],
      servers: [],
      history: mockAlertHistory,
      isLoading: false,
      error: null,
    }),
}))

// Selectors
export const selectActiveAlerts = (state: PerformanceAlertStore) => state.alerts.filter((a) => a.status === "active")

export const selectInactiveAlerts = (state: PerformanceAlertStore) =>
  state.alerts.filter((a) => a.status === "inactive")

export const selectCriticalAlerts = (state: PerformanceAlertStore) =>
  state.alerts.filter((a) => (a.current_value / a.threshold) * 100 >= 100)

export const selectAlertsByMetric = (state: PerformanceAlertStore, metricType: string) =>
  state.alerts.filter((a) => a.metric_type === metricType)

export const selectAvgThreshold = (state: PerformanceAlertStore) => {
  const active = state.alerts.filter((a) => a.status === "active")
  return active.length > 0 ? active.reduce((sum, a) => sum + a.threshold, 0) / active.length : 0
}
